import {
  mdiAccountEdit,
  mdiCar,
  mdiSofa,
  mdiCaravan,
  mdiHomeCityOutline,
  mdiHiking,
  mdiHomeClock,
  mdiAccountHardHat,
  mdiHomeSwitchOutline,
  mdiMusic,
  mdiText,
  mdiViewDashboard,
} from "@mdi/js";
import Icon from "@mdi/react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Toolbar,
  Typography,
} from "@mui/material";
import AppBar from "@mui/material/AppBar";
import React from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import { useUser } from "../../contexts/UserContext";

interface SideNavProps {
  menuOpen: boolean;
  showAdminBoard: boolean;
  handleDrawerToggle: () => void;
  handleDrawerTransitionEnd: () => void;
  handleDrawerClose: () => void;
  drawerWidth: number;
}

const SideNav: React.FC<SideNavProps> = ({
  menuOpen,
  showAdminBoard,
  handleDrawerToggle,
  handleDrawerTransitionEnd,
  handleDrawerClose,
  drawerWidth,
}) => {
  const { userId, userRole } = useUser();

  const getRolePath = (userRole: string): string => {
    if (userRole.includes("admin")) {
      return "admin";
    } else if (userRole.includes("moderator")) {
      return "mod";
    } else if (userRole.includes("user")) {
      return "app";
    } else {
      return "/home"; // default path for other roles
    }
  };

  const icons = [
    mdiText,
    mdiHomeCityOutline,
    mdiHomeClock,
    mdiSofa,
    mdiHomeSwitchOutline,
    mdiCaravan,
    mdiCar,
    mdiHiking,
    mdiAccountHardHat,
  ];
  const adminIcons = [mdiAccountEdit, mdiMusic];

  const renderNavigationLink = (
    to: string,
    id: string,
    defaultMessage: string,
    icon: string
  ) => (
    <>
      <Icon path={icon} size={2} color="black" title={defaultMessage} />

      <ListItemButton onClick={handleDrawerToggle}>
        <Link
          id={`nav-${id}-link`}
          to={to}
          className="nav-link"
          style={{ width: "100%", height: "100%" }}
        >
          <FormattedMessage id={id} defaultMessage={defaultMessage} />
        </Link>
      </ListItemButton>
    </>
  );

  const drawerLinks = (
    <Box sx={{ marginLeft: "5px" }}>
      <Toolbar>
        <IconButton
          onClick={handleDrawerToggle}
          sx={{ display: { xs: "block", sm: "none" } }}
        >
          <ChevronLeftIcon />
        </IconButton>
      </Toolbar>
      <Divider />
      <List>
        {userId ? (
          // Show user-specific links if `userId` is available
          [
            "posts",
            "apartments",
            "short-stays",
            "couchsurfing",
            "house-swap",
            "rvs",
            "cars",
            "travelers",
            "jobs",
          ].map((text, index) => (
            <ListItem key={text} disablePadding>
              {renderNavigationLink(
                `/${getRolePath(userRole)}/${text}`,
                `${text}`,
                `${text}`,
                icons[index]
              )}
            </ListItem>
          ))
        ) : (
          // Show "login" and "register" links if `userId` is not available
          [
            "login",
            "register",
          ].map((text, index) => (
            <ListItem key={text} disablePadding>
              {renderNavigationLink(
                `/${text}`,
                `${text}`,
                `${text}`,
                icons[index]
              )}
            </ListItem>
          ))
        )}
      </List>
      <Divider />
      {userId && showAdminBoard && (
        <>
          <Typography variant="subtitle1">
            <FormattedMessage id="admin-actions" />
          </Typography>
          <List>
            {["users-and-roles", "add-song"].map((text, index) => (
              <ListItem key={text} disablePadding>
                {renderNavigationLink(
                  `/${getRolePath(userRole)}/${text}`,
                  `${text}`,
                  `${text}`,
                  adminIcons[index]
                )}
              </ListItem>
            ))}
          </List>
        </>
      )}

      <List
        sx={{
          display: { xs: "block", md: "none" },
        }}
      >
        {userId && (
          <ListItem>
            {renderNavigationLink(
              `/${getRolePath(userRole)}/`,
              `${getRolePath(userRole)}`,
              `${getRolePath(userRole)}/ Board`,
              mdiViewDashboard
            )}
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {/* Side Nav Drawer, mobile screen */}
        <Drawer
          variant="temporary"
          open={menuOpen}
          onTransitionEnd={handleDrawerTransitionEnd}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawerLinks}
        </Drawer>
        {/* Side Nav Drawer, larger screen */}
        {userId && 
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
          open
        >
          {drawerLinks}
        </Drawer>
        }
      </Box>
    </AppBar>
  );
};

export default SideNav;
